import { useRef, useState, type ReactNode } from 'react';
import { Button } from '../Button/Button';
import { Modal } from './Modal';
import styles from './ConfirmDialog.module.css';

export interface ConfirmDialogProps {
  open: boolean;
  /** Called on cancel/Escape, and after onConfirm resolves. */
  onClose: () => void;
  /**
   * Performs the action. If it returns a Promise the confirm button shows a loading state; the
   * dialog closes when it resolves and stays open (showing `errorMessage`) when it rejects.
   */
  onConfirm: () => void | Promise<unknown>;
  title: ReactNode;
  description?: ReactNode;
  children?: ReactNode;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'default' | 'danger';
  /** Shown inline when onConfirm rejects. */
  errorMessage?: ReactNode;
}

export function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  description,
  children,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  tone = 'default',
  errorMessage = 'The action could not be completed. Please try again.',
}: ConfirmDialogProps) {
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const cancelRef = useRef<HTMLButtonElement>(null);
  const confirmRef = useRef<HTMLButtonElement>(null);

  // Reset transient state whenever the dialog is (re)opened.
  const [wasOpen, setWasOpen] = useState(open);
  if (open !== wasOpen) {
    setWasOpen(open);
    if (open) {
      setPending(false);
      setFailed(false);
    }
  }

  function close() {
    if (pending) return;
    onClose();
  }

  async function confirm() {
    if (pending) return;
    setFailed(false);
    let result: unknown;
    try {
      result = onConfirm();
    } catch {
      setFailed(true);
      return;
    }
    if (!(result instanceof Promise)) {
      onClose();
      return;
    }
    setPending(true);
    try {
      await result;
      setPending(false);
      onClose();
    } catch {
      setPending(false);
      setFailed(true);
    }
  }

  return (
    <Modal
      open={open}
      onClose={close}
      title={title}
      description={description}
      size="sm"
      role="alertdialog"
      dismissible={!pending}
      hideCloseButton
      // Destructive actions start on Cancel so Enter does not trigger them by accident.
      initialFocusRef={tone === 'danger' ? cancelRef : confirmRef}
      footer={
        <>
          <Button ref={cancelRef} variant="secondary" onClick={close} disabled={pending}>
            {cancelLabel}
          </Button>
          <Button
            ref={confirmRef}
            variant={tone === 'danger' ? 'danger' : 'primary'}
            loading={pending}
            onClick={() => void confirm()}
          >
            {confirmLabel}
          </Button>
        </>
      }
    >
      {children}
      {failed && (
        <p role="alert" className={styles.error}>
          {errorMessage}
        </p>
      )}
    </Modal>
  );
}
